'use client'

import React, { FormEvent, useState } from 'react'

import { Rating, RoundedStar } from '@smastrom/react-rating'

import {
  Text,
  Flex,
  AspectRatio,
  Input,
  Textarea,
  Box,
  Container,
} from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { Stack } from '@chakra-ui/react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { getTrackingStatus } from '@/_utils/getStatus'

import reviewService from '@/_service/review.service'
import BottomSheet from '@/_components/Sheet'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { IProps } from '../_types'
import { toaster } from '@/components/ui/toaster'
import { containsReviewUrl } from '@/_utils/review-text'

function Mobile({ review, productId, orderData, product, refetch }: IProps) {
  const [rate, setRate] = useState(5)
  const [isLoading, setLoading] = useState(false)
  const [isOpen, setOpenModal] = useState(false)
  const [reviewMsg, setReviewMsg] = useState('')
  const [clientMail, setClientMail] = useState('')

  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const ratingLabels = t.raw('rating_labels')

  const onSelectRate = (newValue: any) => {
    setRate(newValue)
    // setOpenModal(true)
  }

  const onClose = () => {
    setOpenModal(false)
  }

  const onSubmitReview = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (containsReviewUrl(reviewMsg)) {
      toaster.warning({
        title: t('review_links_not_allowed'),
      })
      return
    }

    setLoading(true)

    const data = {
      branch_id: orderData?.steps?.[0]?.branch_id,
      branch_name: orderData?.steps?.[0]?.branch_name,
      client_id: orderData?.client_id,
      client_mail: clientMail,
      client_name: orderData?.client_name,
      courier_id: orderData?.courier_id,
      courier_name: orderData?.courier_id
        ? `${orderData?.courier?.first_name} ${orderData?.courier?.last_name}`
        : '',
      client_phone: orderData?.client_phone_number,
      delivery_time: orderData?.delivery_time,
      lang: currentLocale,
      operator_id: orderData?.shipper_user?.id,
      operator_name: orderData?.shipper_user?.name,
      order_id: orderData?.id,
      order_step_id: orderData?.steps?.[0]?.id,
      product_id: productId,
      order_num: orderData?.external_order_id,
      rating: rate,
      related_subject: 'product',
      review_message: reviewMsg,
      type: rate > 3 ? 'like' : 'dislike',
    }

    reviewService
      .create(data)
      .then(() => {
        setOpenModal(false)
        refetch()
      })
      .catch(console.log)
      .finally(() => setLoading(false))
  }

  if (
    orderData?.status_id &&
    getTrackingStatus(orderData?.status_id) !== 'delivered' &&
    getTrackingStatus(orderData?.status_id) !== 'finished'
  ) {
    return null
  }

  return (
    <>
      {!Boolean(review?.id) && (
        <Text
          textDecoration={'underline'}
          cursor={'pointer'}
          onClick={() => setOpenModal(true)}
          color={'#1570EF'}
        >
          {t('leave_feedback')}
        </Text>
      )}

      <BottomSheet zIndex={100} isOpen={isOpen} onClose={onClose}>
        <form onSubmit={(e) => onSubmitReview(e)}>
          <Container>
            <Box mb={4}>
              <Text color={'text'} fontSize={24} fontWeight={600}>
                {t('leave_feedback')}
              </Text>
            </Box>
            <Box color={'text'}>
              <Flex gap={4} alignItems={'center'}>
                <Box w={'63px'} h={'63px'} rounded={12} bg={'background'}>
                  {product?.image && (
                    <AspectRatio
                      minW='63px'
                      ratio={1 / 1}
                      overflow='hidden'
                      rounded={12}
                      position='relative'
                    >
                      <Image
                        src={process.env.BASE_URL + product?.image}
                        alt={product?.name}
                        style={{ objectFit: 'cover' }}
                        fill
                      />
                    </AspectRatio>
                  )}
                </Box>
                <Text fontSize={20} fontWeight={500}>
                  {product?.name}
                </Text>
              </Flex>
              <Flex
                py={4}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Rating
                  readOnly={Boolean(review?.id)}
                  style={{ maxWidth: 120 }}
                  value={!review?.id ? rate : review?.rating}
                  itemStyles={{
                    itemShapes: RoundedStar,
                    activeFillColor: '#FF9910',
                    inactiveFillColor: '#E0E8F1',
                  }}
                  onChange={onSelectRate}
                />
                <Text fontSize={12} color={'#A5A5A5'}>
                  {ratingLabels[rate - 1]}
                </Text>
              </Flex>
              <Stack gap={3}>
                <Input
                  type='email'
                  value={clientMail}
                  rounded={12}
                  size={'lg'}
                  placeholder={t('email')}
                  onChange={(e) => setClientMail(e.target.value)}
                  _focus={{
                    outline: 'var(--primary-default)',
                    borderColor: 'var(--primary-default)',
                    boxShadow: '0px 0px 0px 4px #7E5FA60F',
                  }}
                  _active={{
                    outline: 'var(--primary-default)',
                    borderColor: 'var(--primary-default)',
                    boxShadow: '0px 0px 0px 4px #7E5FA60F',
                  }}
                  boxShadow='0px 1px 2px 0px #1018280D'
                  border='1px solid #D0D5DD'
                />
                <Textarea
                  value={reviewMsg}
                  rounded={12}
                  size={'lg'}
                  placeholder={t('your_review')}
                  onChange={(e) => setReviewMsg(e.target.value)}
                  _focus={{
                    outline: 'var(--primary-default)',
                    borderColor: 'var(--primary-default)',
                    boxShadow: '0px 0px 0px 4px #7E5FA60F',
                  }}
                  _active={{
                    outline: 'var(--primary-default)',
                    borderColor: 'var(--primary-default)',
                    boxShadow: '0px 0px 0px 4px #7E5FA60F',
                  }}
                  boxShadow='0px 1px 2px 0px #1018280D'
                  border='1px solid #D0D5DD'
                />
              </Stack>
            </Box>
            <Flex my={4} justifyContent='center'>
              <Button
                type='submit'
                rounded={12}
                size={'xl'}
                w={'100%'}
                bg='primary'
                color='primary.text'
                _hover={{
                  bg: 'primary.hover',
                }}
                loading={isLoading}
                disabled={!Boolean(rate)}
              >
                {t('save')}
              </Button>
            </Flex>
          </Container>
        </form>
      </BottomSheet>
    </>
  )
}

export default Mobile
